import { Link } from "react-router-dom";
import { Facebook, Linkedin, Twitter, Youtube, Mail } from "lucide-react";
import { LogoMark, LogoWord } from "@/components/Logo";

const SOCIALS = [
  { platform: "Facebook", url: "https://www.facebook.com/tripsee.io", Icon: Facebook },
  { platform: "LinkedIn", url: "https://www.linkedin.com/company/tripseellc", Icon: Linkedin },
  { platform: "X", url: "https://www.x.com/Tripseeio", Icon: Twitter },
  { platform: "YouTube", url: "https://www.youtube.com/@LetsGetTripsee", Icon: Youtube },
];

const LEGAL = [
  { title: "Privacy Policy", path: "/privacy-policy", slug: "privacy-policy" },
  { title: "Terms of Use", path: "/terms-of-use", slug: "terms-of-use" },
  { title: "EULA", path: "/eula", slug: "eula" },
  { title: "Legal Disclaimer", path: "/legal-disclaimer", slug: "legal-disclaimer" },
];

const NAV = [
  { title: "Home", path: "/" },
  { title: "Features", path: "/features" },
  { title: "About", path: "/about" },
  { title: "FAQs", path: "/faqs" },
];

export default function Footer() {
  return (
    <footer className="relative border-t border-white/10 bg-[#08090B]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Link to="/" className="flex items-center gap-2.5">
              <LogoMark />
              <LogoWord className="text-2xl" />
            </Link>
            <p className="mt-4 text-sm text-slate-400 leading-relaxed max-w-sm">
              The AI door-to-gate travel companion. Real-time flight, traffic, TSA, and weather
              intelligence, so you leave home at exactly the right minute.
            </p>
            <div className="mt-6 flex items-center gap-3">
              {SOCIALS.map(({ platform, url, Icon }) => (
                <a
                  key={platform}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={platform}
                  data-testid={`footer-social-${platform.toLowerCase()}`}
                  className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-slate-400 hover:text-primary hover:border-primary/40 transition-colors duration-200"
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
            <a
              href="mailto:support@tripsee.io"
              data-testid="footer-support-email"
              className="mt-5 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-primary transition-colors"
            >
              <Mail size={14} /> support@tripsee.io
            </a>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-mono text-xs tracking-widest uppercase text-slate-500 mb-5">Explore</h4>
            <ul className="space-y-3">
              {NAV.map((n) => (
                <li key={n.path}>
                  <Link
                    to={n.path}
                    data-testid={`footer-nav-${n.title.toLowerCase()}`}
                    className="text-sm text-slate-300 hover:text-primary transition-colors"
                  >
                    {n.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="font-mono text-xs tracking-widest uppercase text-slate-500 mb-5">Legal</h4>
            <ul className="space-y-3">
              {LEGAL.map((l) => (
                <li key={l.path}>
                  <Link
                    to={l.path}
                    data-testid={`footer-legal-link-${l.slug}`}
                    className="text-sm text-slate-300 hover:text-primary transition-colors"
                  >
                    {l.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-slate-500">Copyright © 2026 Tripsee. All Rights Reserved.</p>
          <p className="font-mono text-[11px] tracking-widest text-slate-600">
            ARRIVE ON TIME. TRAVEL BETTER. LIVE MORE.
          </p>
        </div>
      </div>
    </footer>
  );
}
